/**
 * Keycloak Integration for Stroyma Public Site
 */

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.flowcore.io';
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'memory-mesh';
const KEYCLOAK_ISSUER = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`;
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'stroyma-app';
const CLIENT_SECRET = import.meta.env.VITE_KEYCLOAK_CLIENT_SECRET || '';
const REDIRECT_URI = `${window.location.origin}/callback`;

let accessToken = null;
let refreshToken = null;
let tokenPayload = null;
let refreshTimer = null;

/**
 * Initialize Keycloak and check for existing session
 */
export async function initKeycloak() {
  // Check if we have tokens in localStorage (persistent across tabs)
  const storedAccessToken = localStorage.getItem('keycloak_token');
  const storedRefreshToken = localStorage.getItem('keycloak_refresh_token');

  if (storedAccessToken) {
    accessToken = storedAccessToken;
    refreshToken = storedRefreshToken;
    tokenPayload = decodeJWT(accessToken);
    if (!isTokenForCurrentRealm(tokenPayload)) {
      console.warn('[Keycloak] Stored token belongs to a different realm, clearing session');
      clearTokens();
      return false;
    }

    // Check if token is expired (with 30 second grace period for clock skew)
    const now = Date.now() / 1000;
    const gracePeriod = 30;
    const isExpired = tokenPayload.exp && tokenPayload.exp < now - gracePeriod;

    console.log('[Keycloak] Token check:', {
      exp: tokenPayload.exp,
      now: now,
      expiresIn: tokenPayload.exp ? tokenPayload.exp - now : 'unknown',
      isExpired,
    });

    if (isExpired) {
      console.log('[Keycloak] Token expired, attempting refresh...');
      // Token expired, try to refresh
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        // Refresh failed, clear tokens
        console.warn('[Keycloak] Session expired, please log in again');
        clearTokens();
        return false;
      }
    }

    // Start automatic token refresh
    startTokenRefreshTimer();

    return true;
  }

  return false;
}

/**
 * Start automatic token refresh timer
 */
function startTokenRefreshTimer() {
  // Clear any existing timer
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }

  // Check token expiration every minute
  refreshTimer = setInterval(async () => {
    if (!tokenPayload?.exp) return;

    const now = Date.now() / 1000;
    const timeUntilExpiry = tokenPayload.exp - now;

    // Refresh if less than 2 minutes remaining (changed from 5 minutes)
    if (timeUntilExpiry < 120 && timeUntilExpiry > 0) {
      console.log('[Keycloak] Token expiring soon, refreshing...');
      await refreshAccessToken();
    } else if (timeUntilExpiry <= 0) {
      console.warn('[Keycloak] Token expired, clearing session');
      clearTokens();
      // Reload page to trigger login
      window.location.reload();
    }
  }, 60000); // Check every 60 seconds
}

/**
 * Clear tokens from storage
 */
function clearTokens() {
  accessToken = null;
  refreshToken = null;
  tokenPayload = null;

  // Clear refresh timer
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }

  localStorage.removeItem('keycloak_token');
  localStorage.removeItem('keycloak_refresh_token');
  sessionStorage.removeItem('kc_state');
  sessionStorage.removeItem('kc_nonce');
}

/**
 * Decode JWT token
 */
function decodeJWT(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(decodeBase64UrlUtf8(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

function decodeBase64UrlUtf8(value) {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

  return new TextDecoder('utf-8').decode(bytes);
}

function normalizeText(value) {
  return typeof value === 'string' ? value.normalize('NFC') : value;
}

function isTokenForCurrentRealm(payload) {
  return payload?.iss === KEYCLOAK_ISSUER;
}

/**
 * Initiate login flow
 */
export function login() {
  const state = generateRandomString(32);
  const nonce = generateRandomString(32);

  sessionStorage.setItem('kc_state', state);
  sessionStorage.setItem('kc_nonce', nonce);

  const authUrl =
    `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/auth?` +
    `client_id=${CLIENT_ID}&` +
    `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
    `response_type=code&` +
    `scope=openid profile email&` +
    `state=${state}&` +
    `nonce=${nonce}`;

  window.location.href = authUrl;
}

/**
 * Handle callback from Keycloak
 */
export async function handleCallback() {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');

  // Verify state
  const storedState = sessionStorage.getItem('kc_state');
  if (state !== storedState) {
    throw new Error('Invalid state parameter');
  }

  // Exchange code for tokens
  const tokenUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code: code,
      redirect_uri: REDIRECT_URI,
      ...(CLIENT_SECRET && { client_secret: CLIENT_SECRET }),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to exchange code for tokens');
  }

  const tokens = await response.json();

  // Store tokens
  accessToken = tokens.access_token;
  refreshToken = tokens.refresh_token;
  tokenPayload = decodeJWT(accessToken);
  if (!isTokenForCurrentRealm(tokenPayload)) {
    clearTokens();
    throw new Error('Received token from unexpected Keycloak realm');
  }

  // Debug: Log token expiration
  const expiresIn = tokenPayload.exp ? tokenPayload.exp - Date.now() / 1000 : 'unknown';
  console.log('[Keycloak] Tokens received:', {
    expiresIn: `${Math.floor(expiresIn / 60)} minutes`,
    exp: tokenPayload.exp,
    hasRefreshToken: !!refreshToken,
  });

  localStorage.setItem('keycloak_token', accessToken);
  localStorage.setItem('keycloak_refresh_token', refreshToken);
  sessionStorage.removeItem('kc_state');
  sessionStorage.removeItem('kc_nonce');

  // Start automatic token refresh
  startTokenRefreshTimer();

  return true;
}

/**
 * Refresh access token
 */
export async function refreshAccessToken() {
  if (!refreshToken) {
    console.warn('[Keycloak] No refresh token available');
    return false;
  }

  try {
    console.log('[Keycloak] Refreshing access token...');
    const tokenUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        refresh_token: refreshToken,
        ...(CLIENT_SECRET && { client_secret: CLIENT_SECRET }),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Keycloak] Token refresh failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      clearTokens();
      return false;
    }

    const tokens = await response.json();

    accessToken = tokens.access_token;
    refreshToken = tokens.refresh_token;
    tokenPayload = decodeJWT(accessToken);
    if (!isTokenForCurrentRealm(tokenPayload)) {
      console.warn('[Keycloak] Refreshed token belongs to a different realm');
      clearTokens();
      return false;
    }

    localStorage.setItem('keycloak_token', accessToken);
    localStorage.setItem('keycloak_refresh_token', refreshToken);

    console.log('[Keycloak] Token refreshed successfully');
    return true;
  } catch (error) {
    console.error('[Keycloak] Token refresh error:', error);
    clearTokens();
    return false;
  }
}

/**
 * Logout
 */
export function logout() {
  clearTokens();

  // Redirect to Keycloak logout with correct parameter name
  const logoutUrl =
    `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout?` +
    `post_logout_redirect_uri=${encodeURIComponent(`${window.location.origin}/`)}&` +
    `client_id=${CLIENT_ID}`;

  window.location.href = logoutUrl;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  if (!accessToken) return false;
  if (!isTokenForCurrentRealm(tokenPayload)) {
    clearTokens();
    return false;
  }

  // Check if token is expired
  if (tokenPayload?.exp) {
    const now = Date.now() / 1000;
    const bufferTime = 60; // 60 seconds buffer before actual expiration

    if (tokenPayload.exp < now + bufferTime) {
      // Token expired or about to expire
      return false;
    }
  }

  return true;
}

/**
 * Get access token (with automatic refresh if needed)
 */
export async function getToken() {
  if (!accessToken) return null;
  if (!isTokenForCurrentRealm(tokenPayload)) {
    clearTokens();
    return null;
  }

  // Check if token needs refresh
  if (tokenPayload?.exp) {
    const now = Date.now() / 1000;
    const bufferTime = 300; // Refresh 5 minutes before expiration

    if (tokenPayload.exp < now + bufferTime) {
      console.log('[Keycloak] Token expiring soon, refreshing...');
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        console.error('[Keycloak] Failed to refresh token');
        return null;
      }
    }
  }

  return accessToken;
}

/**
 * Get access token synchronously (no refresh)
 */
export function getTokenSync() {
  return accessToken;
}

/**
 * Get current user info
 */
export async function getCurrentUser() {
  if (!accessToken) return null;

  // Decode token to get user info and roles
  if (tokenPayload) {
    const roles = tokenPayload.realm_access?.roles || [];

    return {
      id: tokenPayload.sub,
      email: normalizeText(tokenPayload.email || tokenPayload.preferred_username),
      name: normalizeText(tokenPayload.name || tokenPayload.preferred_username),
      roles: roles,
    };
  }

  return null;
}

/**
 * Generate random string
 */
function generateRandomString(length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
